// DcaSimulatorView — retrospective Dollar-Cost-Averaging simulator
// for /liquidity-map/dca. Companion view to CyclesMapView. Uses the
// shared ChartShell + FsResponsiveContainer (so it inherits the same
// per-chart fullscreen + zoom behaviour) and the same caveats pattern
// as Cycles ("Lo que esta simulación NO te dice").
//
// Auth: PUBLIC. Matches the backend `/api/liqmap/dca/*` endpoints.
//
// Coin universe: BTC, ETH, SOL, XRP, ZEC (BNB excluded — Kraken history
// <2 years). The disabled BNB pill in the selector is intentional —
// keeps the visual affordance and explains why.
//
// All calculation lives server-side (`POST /api/liqmap/dca/simulate`).
// The FE only renders. This keeps the math single-sourced.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronUp,
  Info,
} from "lucide-react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import { useLanguage } from "../../i18n/LanguageContext";
import { tDca, DCA_COINS } from "../../i18n/dca";
import { tCycles } from "../../i18n/cycles";
import { API, safeJson } from "../../lib/api";
import {
  ChartShell,
  FsResponsiveContainer,
  FULLSCREEN_STYLES,
} from "./ChartShell";

// Coins in the selector (BNB shown but disabled — see _kraken.py).
const ALL_COINS = ["BTC", "ETH", "SOL", "BNB", "XRP", "ZEC"];

// Coin display step (for showing accumulated coin in the summary).
const COIN_DECIMALS = { BTC: 6, ETH: 5, SOL: 3, XRP: 1, ZEC: 4 };

function formatUsd(v) {
  if (v == null || !isFinite(v)) return "—";
  if (Math.abs(v) >= 1_000_000)
    return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  if (Math.abs(v) >= 1) return `$${v.toFixed(2)}`;
  return `$${v.toFixed(4)}`;
}

function formatCoin(v, coin) {
  if (v == null || !isFinite(v)) return "—";
  const d = COIN_DECIMALS[coin] ?? 4;
  return v.toFixed(d);
}

function Pill({ children, tone = "neutral", testId }) {
  const toneClass =
    tone === "warn"
      ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
      : tone === "success"
        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
        : tone === "danger"
          ? "border-rose-500/40 bg-rose-500/10 text-rose-300"
          : tone === "info"
            ? "border-sky-500/40 bg-sky-500/10 text-sky-300"
            : "border-zinc-700 bg-zinc-900/60 text-zinc-300";
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${toneClass}`}
    >
      {children}
    </span>
  );
}

export default function DcaSimulatorView() {
  const { lang } = useLanguage();
  const t = tDca(lang);
  const tCy = tCycles(lang);

  const [coin, setCoin] = useState("BTC");
  const [meta, setMeta] = useState(null); // { earliest_iso, latest_iso, ... }
  const [metaErr, setMetaErr] = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);

  const [startIso, setStartIso] = useState("2020-01-01");
  const [weeklyAmount, setWeeklyAmount] = useState(100);
  const [feePct, setFeePct] = useState(0.2);

  const [result, setResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [simErr, setSimErr] = useState(null);

  const [tableOpen, setTableOpen] = useState(false);

  // Fetch meta whenever coin changes (gives the date-picker its bounds).
  useEffect(() => {
    let alive = true;
    setMetaLoading(true);
    setMetaErr(null);
    setMeta(null);
    fetch(`${API}/liqmap/dca/meta/${coin}`)
      .then(safeJson)
      .then((data) => {
        if (!alive) return;
        setMeta(data);
        // Clamp current startIso into the new range.
        if (data?.earliest_iso && startIso < data.earliest_iso) {
          setStartIso(data.earliest_iso);
        }
      })
      .catch((err) => {
        if (!alive) return;
        setMetaErr(err?.message || "meta_failed");
      })
      .finally(() => alive && setMetaLoading(false));
    return () => {
      alive = false;
    };
  }, [coin]);

  const runSimulation = useCallback(async () => {
    if (!meta) return;
    setSimLoading(true);
    setSimErr(null);
    setResult(null);
    try {
      const resp = await fetch(`${API}/liqmap/dca/simulate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          coin,
          start_iso: startIso,
          weekly_amount_usd: Number(weeklyAmount),
          fee_pct: Number(feePct),
        }),
      });
      if (!resp.ok) {
        const detail = await resp
          .json()
          .then((d) => d?.detail || `HTTP ${resp.status}`)
          .catch(() => `HTTP ${resp.status}`);
        throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
      }
      const data = await resp.json();
      setResult(data);
    } catch (err) {
      setSimErr(err?.message || "simulate_failed");
    } finally {
      setSimLoading(false);
    }
  }, [coin, startIso, weeklyAmount, feePct, meta]);

  function resetForm() {
    setResult(null);
    setSimErr(null);
    setStartIso(meta?.earliest_iso || "2020-01-01");
    setWeeklyAmount(100);
    setFeePct(0.2);
  }

  // Chart data — combine portfolio_value + invested_acc per week.
  const chartData = useMemo(() => {
    if (!result) return [];
    return result.rows.map((r) => ({
      x: r.ts,
      iso: r.iso,
      value: r.portfolio_value_usd,
      invested: r.invested_acc_usd,
    }));
  }, [result]);

  const summary = result?.summary;
  const pnlTone = summary
    ? summary.pnl_abs_usd >= 0
      ? "success"
      : "danger"
    : "neutral";
  const PnlIcon = summary && summary.pnl_abs_usd >= 0 ? TrendingUp : TrendingDown;

  // BNB tooltip handling for the selector.
  function selectorTooltip(c) {
    if (c === "BNB") return t.bnb_disabled_tooltip;
    return undefined;
  }

  return (
    <div
      className="min-h-screen bg-zinc-950 px-4 pb-12 pt-6 text-zinc-100"
      data-testid="dca-simulator-view"
    >
      <style>{FULLSCREEN_STYLES}</style>

      <div className="mx-auto max-w-6xl">
        {/* ── Header ──────────────────────────────────────────── */}
        <div className="mb-4 flex items-center justify-between gap-2">
          <Link
            to="/liquidity-map"
            data-testid="dca-back-link"
            className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
          >
            <ArrowLeft className="h-3 w-3" />
            {t.back_to_liqmap}
          </Link>
          <Link
            to="/liquidity-map/cycles"
            data-testid="dca-open-cycles-link"
            title={t.open_cycles_title}
            className="inline-flex items-center gap-1 rounded-md border border-amber-400/55 bg-zinc-900 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-300 transition hover:bg-amber-400/10"
          >
            {t.open_cycles}
          </Link>
        </div>

        <h1 className="text-2xl font-semibold text-zinc-100">{t.title}</h1>
        <p className="mt-1 text-sm text-zinc-400">{t.subtitle}</p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Pill tone="neutral" testId="dca-source-pill">
            {t.source_label}
          </Pill>
          {meta && (
            <Pill tone="info" testId="dca-meta-range">
              {meta.earliest_iso} → {meta.latest_iso} ({meta.bars} {lang === "es" ? "semanas" : lang === "sk" ? "týždňov" : "weeks"})
            </Pill>
          )}
        </div>

        {/* ── Form ────────────────────────────────────────────── */}
        <div
          data-testid="dca-form"
          className="mt-6 rounded-xl border border-zinc-800 bg-zinc-950/60 p-4"
        >
          <h2 className="mb-3 text-sm font-semibold text-zinc-300">
            {t.form_title}
          </h2>

          <div className="grid gap-3 md:grid-cols-4">
            {/* Coin selector */}
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">
                {t.form_coin}
              </label>
              <div className="flex flex-wrap gap-1" data-testid="dca-coin-selector">
                {ALL_COINS.map((c) => {
                  const isDisabled = !DCA_COINS.includes(c);
                  const isActive = c === coin && !isDisabled;
                  return (
                    <button
                      key={c}
                      type="button"
                      data-testid={`dca-coin-${c.toLowerCase()}`}
                      onClick={() => !isDisabled && setCoin(c)}
                      disabled={isDisabled}
                      title={selectorTooltip(c)}
                      className={`rounded-md border px-2.5 py-1 text-xs font-mono transition ${
                        isActive
                          ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                          : isDisabled
                            ? "cursor-not-allowed border-zinc-800 text-zinc-600"
                            : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500 hover:text-white"
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Start date */}
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">
                {t.form_start}
              </label>
              <input
                type="date"
                data-testid="dca-start-input"
                value={startIso}
                min={meta?.earliest_iso || undefined}
                max={meta?.latest_iso || undefined}
                onChange={(e) => setStartIso(e.target.value)}
                className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 focus:border-emerald-500/60 focus:outline-none"
              />
              {meta?.earliest_iso && (
                <p className="mt-1 text-[10px] text-zinc-500">
                  {t.form_start_help.replace("{date}", meta.earliest_iso)}
                </p>
              )}
            </div>

            {/* Weekly amount */}
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">
                {t.form_amount}
              </label>
              <input
                type="number"
                data-testid="dca-amount-input"
                value={weeklyAmount}
                min={1}
                max={100000}
                step={1}
                onChange={(e) => setWeeklyAmount(e.target.value)}
                className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 focus:border-emerald-500/60 focus:outline-none"
              />
            </div>

            {/* Fee */}
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">
                {t.form_fee}
              </label>
              <input
                type="number"
                data-testid="dca-fee-input"
                value={feePct}
                min={0}
                max={1}
                step={0.01}
                onChange={(e) => setFeePct(e.target.value)}
                className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 focus:border-emerald-500/60 focus:outline-none"
              />
              <p className="mt-1 text-[10px] text-zinc-500">{t.form_fee_help}</p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="dca-submit-btn"
              onClick={runSimulation}
              disabled={!meta || simLoading || metaLoading}
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/60 bg-emerald-500/10 px-3 py-1.5 text-sm font-semibold text-emerald-300 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {simLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {simLoading ? t.simulating : t.form_submit}
            </button>
            <button
              type="button"
              data-testid="dca-reset-btn"
              onClick={resetForm}
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-300 transition hover:border-zinc-500 hover:text-white"
            >
              {t.form_reset}
            </button>
            {metaLoading && (
              <span className="text-xs text-zinc-500" data-testid="dca-meta-loading">
                {t.loading}
              </span>
            )}
            {metaErr && (
              <span className="text-xs text-rose-400" data-testid="dca-meta-error">
                {t.err_load}
              </span>
            )}
            {simErr && (
              <span className="text-xs text-rose-400" data-testid="dca-sim-error">
                {t.err_simulate}: {simErr}
              </span>
            )}
          </div>
        </div>

        {/* ── Result section ──────────────────────────────────── */}
        {!result && !simLoading && (
          <div
            data-testid="dca-no-data"
            className="mt-6 rounded-xl border border-zinc-800 bg-zinc-950/60 p-6 text-center text-sm text-zinc-400"
          >
            {t.no_data_yet}
          </div>
        )}

        {result && (
          <>
            {/* Summary KPI cards */}
            <div
              data-testid="dca-summary-cards"
              className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
            >
              <SummaryCard
                testId="dca-summary-invested"
                label={t.summary_invested}
                help={t.summary_invested_help}
                value={formatUsd(summary.invested_gross_usd)}
                sub={t.summary_weeks.replace("{n}", summary.weeks)}
                tone="neutral"
              />
              <SummaryCard
                testId="dca-summary-value"
                label={t.summary_current_value}
                help={t.summary_current_value_help}
                value={formatUsd(summary.current_value_usd)}
                sub={t.summary_current_price.replace(
                  "{price}",
                  formatUsd(summary.current_price_usd),
                )}
                tone="info"
              />
              <SummaryCard
                testId="dca-summary-pnl"
                label={t.summary_pnl}
                value={formatUsd(summary.pnl_abs_usd)}
                sub={t.summary_pnl_pct.replace("{pct}", summary.pnl_pct.toFixed(2))}
                tone={pnlTone}
                icon={<PnlIcon className="h-3.5 w-3.5" />}
              />
              <SummaryCard
                testId="dca-summary-coin"
                label={t.summary_coin_total.replace("{coin}", coin)}
                value={formatCoin(summary.coin_total, coin)}
                sub={
                  summary.avg_cost_basis_usd != null
                    ? `${t.summary_avg_cost.replace("{coin}", coin)}: ${formatUsd(summary.avg_cost_basis_usd)}`
                    : null
                }
                tone="neutral"
              />
            </div>

            {/* Fees + buy-range footnote */}
            <div
              data-testid="dca-summary-footer"
              className="mt-3 flex flex-wrap gap-3 text-[11px] text-zinc-500"
            >
              <span>
                {t.summary_fees_total}: {formatUsd(summary.fees_total_usd)}
              </span>
              <span>{t.summary_first_buy.replace("{date}", summary.first_buy_iso)}</span>
              <span>{t.summary_last_buy.replace("{date}", summary.last_buy_iso)}</span>
            </div>

            {/* Chart */}
            <div className="mt-6">
              <ChartShell
                testId="dca-chart-wrapper"
                title={t.chart_title}
                subtitle={t.chart_subtitle}
              >
                <FsResponsiveContainer width="100%" height={360}>
                  <ComposedChart
                    data={chartData}
                    margin={{ top: 16, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="x"
                      type="number"
                      scale="time"
                      domain={["dataMin", "dataMax"]}
                      tickFormatter={(v) =>
                        new Date(v * 1000).getUTCFullYear()
                      }
                      stroke="#71717a"
                      tick={{ fill: "#a1a1aa", fontSize: 11 }}
                    />
                    <YAxis
                      tickFormatter={(v) =>
                        v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`
                      }
                      stroke="#71717a"
                      tick={{ fill: "#a1a1aa", fontSize: 11 }}
                      label={{
                        value: t.chart_axis_y_usd,
                        position: "left",
                        angle: -90,
                        offset: 10,
                        fill: "#71717a",
                        fontSize: 10,
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#18181b",
                        border: "1px solid #3f3f46",
                        borderRadius: "6px",
                        fontSize: 12,
                      }}
                      labelFormatter={(v) =>
                        new Date(v * 1000).toISOString().slice(0, 10)
                      }
                      formatter={(v, name) => [formatUsd(v), name]}
                    />
                    <Legend
                      verticalAlign="top"
                      iconSize={10}
                      wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }}
                    />
                    <Line
                      type="monotone"
                      dataKey="invested"
                      name={t.chart_legend_invested}
                      stroke="#71717a"
                      strokeWidth={1.5}
                      strokeDasharray="4 4"
                      dot={false}
                      isAnimationActive={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      name={t.chart_legend_value}
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </ComposedChart>
                </FsResponsiveContainer>
              </ChartShell>
            </div>

            {/* Table */}
            <div
              data-testid="dca-table-section"
              className="mt-2 rounded-xl border border-zinc-800 bg-zinc-950/60 p-4"
            >
              <button
                type="button"
                data-testid="dca-table-toggle"
                onClick={() => setTableOpen((o) => !o)}
                className="flex items-center gap-1.5 text-xs font-semibold text-zinc-300 hover:text-white"
              >
                {tableOpen ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                {tableOpen
                  ? t.table_expanded
                  : t.table_collapsed.replace("{n}", result.rows.length)}
              </button>
              {tableOpen && (
                <div
                  data-testid="dca-table-wrapper"
                  className="mt-3 max-h-96 overflow-auto rounded-md border border-zinc-800"
                >
                  <table className="min-w-full text-xs">
                    <thead className="sticky top-0 z-10 bg-zinc-900 text-zinc-400">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">
                          {t.table_col_date}
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          {t.table_col_price}
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          {t.table_col_invested}
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          {t.table_col_fee}
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          {t.table_col_coin_bought}
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          {t.table_col_coin_acc}
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          {t.table_col_value}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800 text-zinc-300">
                      {result.rows.map((r, i) => (
                        <tr
                          key={r.ts}
                          data-testid={`dca-table-row-${i}`}
                          className="hover:bg-zinc-900/60"
                        >
                          <td className="px-3 py-1.5 font-mono">{r.iso}</td>
                          <td className="px-3 py-1.5 text-right font-mono">
                            {formatUsd(r.price)}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono">
                            {formatUsd(r.net_invested_usd)}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono text-zinc-500">
                            {formatUsd(r.fee_usd)}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono">
                            {formatCoin(r.coin_bought, coin)}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono text-zinc-100">
                            {formatCoin(r.coin_acc, coin)}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono">
                            {formatUsd(r.portfolio_value_usd)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Caveats — honest disclaimers ─────────────────────── */}
        <div
          data-testid="dca-caveats-panel"
          className="mt-8 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4"
        >
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-300">
            <Info className="h-4 w-4" />
            {t.caveats_panel_title}
          </div>
          <p className="mb-2 text-xs text-zinc-400">{t.caveats_panel_intro}</p>
          <ul className="list-inside list-disc space-y-1 text-xs text-zinc-300">
            <li data-testid="dca-caveat-retrospective">{t.caveats_retrospective}</li>
            <li data-testid="dca-caveat-cherry-picking">{t.caveats_cherry_picking}</li>
            <li data-testid="dca-caveat-no-slippage">{t.caveats_no_slippage}</li>
            <li data-testid="dca-caveat-fees-estimated">{t.caveats_fees_estimated}</li>
            <li data-testid="dca-caveat-no-taxes">{t.caveats_no_taxes}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ testId, label, help, value, sub, tone, icon }) {
  const toneRing =
    tone === "success"
      ? "border-emerald-500/40"
      : tone === "danger"
        ? "border-rose-500/40"
        : tone === "info"
          ? "border-sky-500/40"
          : "border-zinc-800";
  const toneText =
    tone === "success"
      ? "text-emerald-300"
      : tone === "danger"
        ? "text-rose-300"
        : tone === "info"
          ? "text-sky-300"
          : "text-zinc-100";
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border ${toneRing} bg-zinc-950/60 p-4`}
    >
      <div className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-zinc-500">
        <span>{label}</span>
        {help && (
          <span title={help} className="cursor-help text-zinc-600">
            ⓘ
          </span>
        )}
      </div>
      <div
        className={`mt-1 inline-flex items-center gap-1.5 text-xl font-semibold ${toneText}`}
      >
        {icon}
        {value}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-zinc-500">{sub}</div>}
    </div>
  );
}
